import { Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazyWithReload } from '@shared/lazyWithReload';
import ProtectedRoute from '@auth/presentation/components/ProtectedRoute';
import InactiveUserPage from '@shared/components/InactiveUserPage';
import RoleRedirect from '@shared/components/RoleRedirect';
import ConnectionRedirect from '@shared/components/ConnectionRedirect';
import AdminLayout from '@admin/layout/AdminLayout';
import OrgLayout from '@organization/layout/OrgLayout';
import { adminRoutes } from '@admin/adminRoutes';
import { orgRoutes } from '@organization/orgRoutes';
import { LoadingSpinner } from '@klonup/design-system';
import RouteErrorFallback from '@shared/components/RouteErrorFallback';
import { useStripLngQueryParam } from '@shared/infrastructure/i18n/useStripLngQueryParam';

// Wrapper para lazy loading con Suspense
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
    {children}
  </Suspense>
);

// @auth: Dominio de Autenticación
const LoginPage = lazyWithReload(() => import('@auth/presentation/pages/LoginPage'));
const ForgotPasswordPage = lazyWithReload(() => import('@auth/presentation/pages/ForgotPasswordPage'));
// @signup: Alta autónoma de organización
const SignupPage = lazyWithReload(() => import('@/signup/presentation/pages/SignupPage'));
// @plan-selection: Página pública de selección de plan (pricing)
const PlanSelectionPage = lazyWithReload(() => import('@/plan-selection/presentation/pages/PlanSelectionPage'));
// @plan-selection: Página pública post-pago — success_url de Stripe redirige aquí (D1)
const CheckoutCompletePage = lazyWithReload(() => import('@/plan-selection/presentation/pages/CheckoutCompletePage'));
const SetPasswordPage = lazyWithReload(() => import('@auth/presentation/pages/SetPasswordPage'));
const VerifyEmailLinkPage = lazyWithReload(() => import('@auth/presentation/pages/VerifyEmailLinkPage'));

import { useAuthStore } from '@auth/presentation/store/authStore';
import { orgPaths } from '@shared/routes/appPaths';

// Guards de contexto
export function AdminGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);

  if (!user || user.role !== 'superadmin') {
    if (user?.organizationSlug) {
      return <Navigate to={orgPaths.myOrganization(user.organizationSlug)} replace />;
    }
    return <Navigate to="/auth/login" replace />;
  }
  return <>{children}</>;
}

export function OrgGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);

  if (!user || user.role === 'superadmin') {
    return <Navigate to="/admin/organizations" replace />;
  }
  return <>{children}</>;
}

// RootLayout — único punto de montaje global del mecanismo de idioma por query param (`lng`,
// fix-lang-query-param-override). Envuelve TODAS las rutas top-level para que
// useStripLngQueryParam() se ejecute una única vez, independientemente de a qué ruta aterrice
// el usuario — ninguna página implementa su propia lógica de idioma.
export function RootLayout() {
  useStripLngQueryParam();
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      // Ruta pública de signup — sin ProtectedRoute (D14)
      {
        path: '/free-trial',
        element: <LazyPage><SignupPage /></LazyPage>,
        errorElement: <RouteErrorFallback />,
      },

      // Ruta pública de pricing — sin ProtectedRoute (accesible también logado para upgrade de trial)
      {
        path: '/pricing',
        element: <LazyPage><PlanSelectionPage /></LazyPage>,
        errorElement: <RouteErrorFallback />,
      },

      // Ruta pública post-pago — sin ProtectedRoute: el comprador anónimo aterriza aquí sin
      // sesión (post-payment-landing, D5). success_url de Stripe redirige a esta ruta con
      // `?session_id=...` tras completar el Checkout.
      {
        path: '/checkout/complete',
        element: <LazyPage><CheckoutCompletePage /></LazyPage>,
        errorElement: <RouteErrorFallback />,
      },

      // Rutas de Autenticación (Sin Layout Principal)
      {
        path: '/auth',
        errorElement: <RouteErrorFallback />,
        children: [
          { path: 'login', element: <LazyPage><LoginPage /></LazyPage> },
          { path: 'forgot-password', element: <LazyPage><ForgotPasswordPage /></LazyPage> },
          { path: 'set-password', element: <LazyPage><SetPasswordPage /></LazyPage> },
          { path: 'verify-email-link', element: <LazyPage><VerifyEmailLinkPage /></LazyPage> },
        ],
      },

      // Contexto Superadmin: /admin/*
      {
        path: '/admin',
        element: (
          <ProtectedRoute>
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
        children: adminRoutes,
      },

      // Contexto Organización: /organization/:slug/*
      {
        path: '/organization/:slug',
        element: (
          <ProtectedRoute>
            <OrgGuard>
              <OrgLayout />
            </OrgGuard>
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
        children: orgRoutes,
      },

      // Ruta raíz — redirige según rol
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <RoleRedirect />
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
      },

      // Ruta neutra para deep-links de email — resuelve rol→ruta real preservando query+hash.
      // Los emails de resultado de backup enlazan aquí desde h16a (FirestoreBackupResultMailerAdapter).
      {
        path: '/connections/:id',
        element: (
          <ProtectedRoute>
            <ConnectionRedirect />
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
      },

      // Ruta neutra antigua: la siguen usando los emails enviados antes de h16a.
      {
        path: '/klons/:id',
        element: (
          <ProtectedRoute>
            <ConnectionRedirect />
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
      },

      // Cuenta desactivada — ProtectedRoute básico para que usuarios no autenticados vayan al login
      {
        path: '/inactive',
        element: (
          <ProtectedRoute>
            <InactiveUserPage />
          </ProtectedRoute>
        ),
        errorElement: <RouteErrorFallback />,
      },

      // 404
      {
        path: '*',
        element: <div>404 | Página no encontrada</div>,
        errorElement: <RouteErrorFallback />,
      },
    ],
  },
]);

// Listener para detección mid-session de usuario inactivo.
// Si el backend devuelve 403 USER_INACTIVE, fetchWithInterceptor emite este evento
// y navegamos a /inactive sin recargar la página.
window.addEventListener('user-inactive', () => {
  router.navigate('/inactive', { replace: true });
});
